import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import { set } from 'zod';
import { Loader } from '../components/ModalsCommonFunctions';

const LessonMaster = () => {
    const authorizedAxios = generateAxiosInstance()
    const initialFormState = {
        syllabusStructureMasterId: '',
        subjectMasterId: '',
        lessonName: ['']
    }
    const [syllabusList, setSyllabusList] = useState([])
    const [subjectList, setSubjectList] = useState([])
    const [lessonData, setLessonData] = useState(initialFormState)
    const [lessonList, setLessonList] = useState([])
    const [editId, setEditId] = useState(null)

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);
    const [refresh, setRefresh] = useState(true);
    const [addNew, setAddNew] = useState(false);

    const [syllabusNameSort, setSyllabusNameSort] = useState(null);
    const [subjectNameSort, setSubjectNameSort] = useState(null);
    const [lessonNameSort, setLessonNameSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["syllabusStructureMasterDetails.syllabusStructureName", "subjectMasterDetails.subjectName", "lessonName"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            setRefresh(!refresh);
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            if (lessonData.syllabusStructureMasterId !== '' && lessonData.subjectMasterId !== '') {
                let apiUrl = '/get-lesson-master-list?';

                if (lessonData.syllabusStructureMasterId) {
                    apiUrl += `syllabusStructureId=${lessonData.syllabusStructureMasterId}&`;
                }

                if (lessonData.subjectMasterId) {
                    apiUrl += `subjectMasterId=${lessonData.subjectMasterId}&`;
                }

                apiUrl = apiUrl.replace(/&$/, '');
                setLessonList(await searchArray("A", apiUrl, searchKeys, searchValue.trim()))
            }
        }
    }

    useEffect(() => {
        getSyllabusList();
    }, []);

    useEffect(() => {
        if (lessonData.syllabusStructureMasterId !== '' && lessonData.subjectMasterId !== '') {
            let apiUrl = '/get-lesson-master-list?';

            if (lessonData.syllabusStructureMasterId) {
                apiUrl += `syllabusStructureId=${lessonData.syllabusStructureMasterId}&`;
            }

            if (lessonData.subjectMasterId) {
                apiUrl += `subjectMasterId=${lessonData.subjectMasterId}&`;
            }

            apiUrl = apiUrl.replace(/&$/, '');
            authorizedAxios && authorizedAxios.get(apiUrl)
                .then(response => {
                    setLessonList(response.data.result);
                })
                .catch(error => {
                    setLessonList(error?.response?.data.result);
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    console.error('Error fetching data:', errorMsg);
                });
        }
    }, [lessonData, refresh]);

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(lessonList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId, addNew])

    useEffect(() => {
        setTotalPages(Math.ceil(lessonList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > lessonList.length) {
            endIndex = lessonList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(lessonList.slice(startIndex, endIndex));
    }, [lessonList, currPage, itemsPerPage]);

    const getSyllabusList = () => {
        const apiUrlForSyllabus = `/get-syllabus-structure-master`;
        authorizedAxios && authorizedAxios.get(apiUrlForSyllabus)
            .then(response => {
                setSyllabusList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const getLessonList = () => {
        if (lessonData.syllabusStructureMasterId !== '' && lessonData.subjectMasterId !== '') {
            let apiUrl = '/get-lesson-master-list?';

            if (lessonData.syllabusStructureMasterId) {
                apiUrl += `syllabusStructureId=${lessonData.syllabusStructureMasterId}&`;
            }

            if (lessonData.subjectMasterId) {
                apiUrl += `subjectMasterId=${lessonData.subjectMasterId}&`;
            }

            apiUrl = apiUrl.replace(/&$/, '');
            // const apiUrl = `/get-lesson-master-list`;
            authorizedAxios && authorizedAxios.get(apiUrl)
                .then(response => {
                    setLessonList(response.data.result)
                    setAddNew(!addNew)
                    // console.log('Data:', response.data);
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    console.error('Error fetching data:', errorMsg);
                });
        }
    }

    const handleSyllabusChange = (e) => {
        setLessonData((prevData) => {
            return {
                ...prevData,
                [e.target.name]: e.target.value
            }
        })
        const apiUrl = `/syllabus-structure-wise-subjects?id=${e.target.value}`;
        authorizedAxios && authorizedAxios.get(apiUrl)
            .then(response => {
                setLessonData((prevData) => {
                    return {
                        ...prevData,
                        subjectMasterId: ''
                    }
                })
                setSubjectList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleInputChange = (e, index) => {
        setLessonData((prevData) => {
            if (e.target.name === 'lessonName') {
                const updatedLessonName = [...prevData.lessonName];
                updatedLessonName[index] = e.target.value;
                return {
                    ...prevData,
                    lessonName: updatedLessonName,
                };
            } else {
                return {
                    ...prevData,
                    [e.target.name]: e.target.value
                }
            }
        })
    }

    const handleAddLesson = () => {
        setLessonData((prevData) => ({
            ...prevData,
            lessonName: [...prevData.lessonName, ''],
        }));
    };
    const handleRemoveLesson = (index) => {
        setLessonData((prevData) => {
            const updatedLessonName = [...prevData.lessonName];
            updatedLessonName.splice(index, 1);
            return {
                ...prevData,
                lessonName: updatedLessonName,
            }
        });
    };


    //error
    const initialErrorState = {

    }
    const [error, setError] = useState(initialErrorState)

    const validateForm = () => {
        const noErrors = Object.values(error).every((error) => error === '');

        const allValuesValid = Object.entries(lessonData).every(([key, value]) => {
            if (key === 'lessonName' && Array.isArray(value)) {
                return value.every(topic => typeof topic === 'string' && topic.trim() !== '');
            } else {
                return typeof value === 'string' && value.trim() !== '';
            }
        });

        return noErrors && allValuesValid;
    };

    useEffect(() => {
        if (!Object.values(error).every((error) => error === '')) {
            for (const [key, value] of Object.entries(lessonData)) {
                if (key === "lessonName") {
                    for (const lesson of lessonData.lessonName) {
                        if (lesson == '' || lesson.length == 0) {
                            setError((prevData) => ({
                                ...prevData,
                                lessonName: 'This field is required.',
                            }));
                            break;
                        } else {
                            setError((prevData) => {
                                return {
                                    ...prevData,
                                    [key]: '',
                                }
                            });
                        }
                    }
                } else if (value === '' || value.length === 0) {
                    setError((prevData) => {
                        return {
                            ...prevData,
                            [key]: 'This field is required.',
                        }
                    })
                } else {
                    setError((prevData) => {
                        return {
                            ...prevData,
                            [key]: '',
                        }
                    })
                }
            }
        }
    }, [lessonData])

    const addLesson = () => {
        const apiUrl = `/add-lesson-master`;
        if (validateForm()) {
            setLoading(true);
            authorizedAxios && authorizedAxios.post(apiUrl, lessonData)
                .then(response => {
                    getLessonList();
                    setLessonData((prevData) => {
                        return {
                            ...prevData,
                            lessonName: [''],
                        };
                    })

                    setSaveId(response.data.result[0].id);
                    setSyllabusNameSort(null);
                    setSubjectNameSort(null);
                    setLessonNameSort(null);
                    setSearchValue(null);
                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            for (const [key, value] of Object.entries(lessonData)) {
                if (key === "lessonName") {
                    for (const lesson of lessonData.lessonName) {
                        if (lesson == '' || lesson.length == 0) {
                            setError((prevData) => ({
                                ...prevData,
                                lessonName: 'This field is required.',
                            }));
                            break;
                        } else {
                            setError((prevData) => {
                                return {
                                    ...prevData,
                                    [key]: '',
                                }
                            });
                        }
                    }
                } else if (value == '') {
                    setError((prevData) => {
                        return {
                            ...prevData,
                            [key]: 'This field is required.',
                        }
                    })
                } else {
                    setError((prevData) => {
                        return {
                            ...prevData,
                            [key]: '',
                        }
                    })
                }
            }
        }
    }

    const deleteLesson = (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            setLoading(true);
            const apiUrl = `/delete-lesson-master?id=${id}`;
            authorizedAxios && authorizedAxios.delete(apiUrl)
                .then(response => {
                    const page = deleteItem(currPage, itemsPerPage, lessonList.length);
                    setRefresh(!refresh);
                    setCurrPage(page);

                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    console.error('Error fetching data:', errorMsg);
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        }
    }

    const updateLesson = () => {
        setLoading(true);
        const apiUrl = `/edit-lesson-master?id=${editId}`;
        authorizedAxios && authorizedAxios.put(apiUrl, lessonData)
            .then(response => {
                getLessonList();
                setSaveId(response.data.result.id);
                setEditId(null);
                setLessonData((prevData) => {
                    return {
                        ...prevData,
                        lessonName: [''],
                    };
                })
                Swal.fire(
                    { text: response.data.message }
                );
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            })
            .finally(() => {
                setLoading(false); // End loading
            });
    }

    const handleReset = () => {
        setLessonList([]);
        setEditId('');
        setLessonData(initialFormState);
        setSubjectNameSort(null);
        setLessonNameSort(null);
        setSyllabusNameSort(null);
    }

    useEffect(() => {
        if (syllabusNameSort == true || syllabusNameSort == false) {
            setSubjectNameSort(null);
            setLessonNameSort(null);
        }
    }, [syllabusNameSort])

    useEffect(() => {
        if (subjectNameSort == true || subjectNameSort == false) {
            setSyllabusNameSort(null);
            setLessonNameSort(null);
        }
    }, [subjectNameSort])

    useEffect(() => {
        if (lessonNameSort == true || lessonNameSort == false) {
            setSyllabusNameSort(null);
            setSubjectNameSort(null);
        }
    }, [lessonNameSort])


    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Lesson </span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa-solid fa-file-circle-check"></i> Lesson Master</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="inputPassword" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Syllabus Structure :</label>
                                                    <select class="form-select" aria-label="Default select example" name='syllabusStructureMasterId' onChange={handleSyllabusChange} value={lessonData.syllabusStructureMasterId}>
                                                        <option selected value={''} disabled>Select Syllabus</option>
                                                        {
                                                            syllabusList?.map((syllabus) => {
                                                                return <option value={syllabus.id}>{syllabus.syllabusStructureName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    {error.syllabusStructureMasterId && <p style={{ color: 'red' }}>{error.syllabusStructureMasterId}</p>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="inputPassword" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Subject :</label>
                                                    <select class="form-select" aria-label="Default select example" name='subjectMasterId' onChange={handleInputChange} value={lessonData.subjectMasterId}>
                                                        <option selected value={''} disabled>Select Subject</option>
                                                        {
                                                            subjectList?.map((syllabus) => {
                                                                return <option value={syllabus.subjectMasterDetails.id}>{syllabus.subjectMasterDetails.subjectName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    {error.subjectMasterId && <p style={{ color: 'red' }}>{error.subjectMasterId}</p>}

                                                </div>

                                                {lessonData?.lessonName?.map((lesson, index) => (
                                                    <>
                                                        <div className="col-md-5">
                                                            <label for="inputPassword" className="col-form-label"><span style={{ color: "red" }}>*</span>Lesson Name :</label>
                                                            <input type="text" className="form-control" id={`lessonName${index}`} name='lessonName' onChange={(e) => handleInputChange(e, index)} value={lesson} />
                                                            {error.lessonName && lesson == "" && <p style={{ color: 'red' }}>{error.lessonName}</p>}
                                                        </div>
                                                        {lessonData.lessonName.length > 1 && (
                                                            <div className="col-md-1 mt-4 mb-1" style={{ textAlign: 'right' }}>
                                                                <button className="btn btn-primary" type="button" style={{ marginTop: '14px' }} onClick={() => handleRemoveLesson(index)} ><i class="material-icons" style={{ fontSize: '20px' }} >delete</i></button>
                                                            </div>
                                                        )}
                                                    </>
                                                ))}
                                                {!editId && (
                                                    <div className="col-md-12 mt-4 mb-1">
                                                        <div className="col-auto">
                                                            <button className="btn btn-primary" type="button" style={{ width: '233px' }} onClick={handleAddLesson}>+ Add Another Lesson Name</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>

                                                    <div className="col-auto">
                                                        {!editId && <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} onClick={addLesson}>Save</button>
                                                        }                                                        <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleReset}>Reset</button>

                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateLesson} disabled={!editId}>Update</button>)
                                                        }
                                                        {/* <button className="btn btn-outline-secondary" type="button">Close</button> */}
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>

                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope="col">
                                                                            Syllabus Name
                                                                            <SortIcon sortState={syllabusNameSort} setSortState={setSyllabusNameSort} setData={setLessonList} setCurrPage={setCurrPage} sortingPath={"syllabusStructureMasterDetails.syllabusStructureName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Subject Name
                                                                            <SortIcon sortState={subjectNameSort} setSortState={setSubjectNameSort} setData={setLessonList} setCurrPage={setCurrPage} sortingPath={"subjectMasterDetails.subjectName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Lesson Name
                                                                            <SortIcon sortState={lessonNameSort} setSortState={setLessonNameSort} setData={setLessonList} setCurrPage={setCurrPage} sortingPath={"lessonName"} />
                                                                        </th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((lesson, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                        <td>{lesson.syllabusStructureMasterDetails?.syllabusStructureName}</td>
                                                                                        <td>{lesson.subjectMasterDetails.subjectName}</td>

                                                                                        <td>{lesson.lessonName}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" onClick={() => {
                                                                                                setLessonData({
                                                                                                    syllabusStructureMasterId: lesson.syllabusStructureMasterDetails?.id,
                                                                                                    lessonName: [lesson.lessonName],
                                                                                                    subjectMasterId: lesson.subjectMasterDetails.id
                                                                                                });
                                                                                                setEditId(lesson.id);
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === lesson.id} onClick={() => deleteLesson(lesson.id)}> <i class="fa-light fa-trash-can"></i></button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Lesson Available</th>

                                                                            </tr>)
                                                                    }

                                                                </tbody>
                                                            </table>

                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={lessonList.length} />

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default LessonMaster;